import { FC, SyntheticEvent, useReducer, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { MultiValue, PropsValue } from 'react-select';
import Form from 'react-bootstrap/Form';
import { useMutation, useQuery } from '@apollo/client';

import ELEMENT from 'common/element';
import { validEmailFormat } from 'common/functions';

import Button, { ButtonVariant } from 'components/common/Button/Button';
import Input from 'components/common/Input/Input';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { MultiSelectInput } from 'components/common/ReactSelect/MultiSelectInput';
import Select, { OptionType } from 'components/common/ReactSelect/Select';

import { ACCESS_OPTIONS, ACCESS_OPTIONS_MAP, USER_ACCESS_DEFAULT } from '../consts';
import { DASHBOARDS, SEND_INVITES } from '../queries';
import { DashboardItem, USER_ACCESS, UserAccountData } from '../types';
import { createAction } from '../helpers';
import { userFormInitialState, userFormReducer } from '../userFormState';
import '../UserAccountPage.scss';

/**
 * Form that allows admin to add user/set of users with certain access rights

 * @returns {Component}
 */

type UserAddFormProps = {
  disabled?: boolean;
  userAccountData: UserAccountData | undefined;
  refetchData: () => void;
};

export const UserAddForm: FC<UserAddFormProps> = ({ disabled, refetchData, userAccountData }: UserAddFormProps) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [accessErrorValidationMessage, setAccessErrorValidationMessage] = useState<null | string>(null);
  const showSuccessAlert = (): void => {
    setPageAlertConfig({ message: 'Invitation(s) sent', variant: PageAlertVariant.SUCCESS, autoHide: true });
  };
  const [state, dispatch] = useReducer(userFormReducer, userFormInitialState);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const { loading: dashboardLoading, data: dashboardsData } = useQuery(DASHBOARDS);
  const [sendInvites] = useMutation(SEND_INVITES, {
    onCompleted: () => {
      showSuccessAlert();
    },
  });

  const onAccessSelectChangeValidation = (emails: string[]) => {
    const message = emails.length === 0 ? 'Please enter at least one email address' : '';
    setValidationErrorMessage(message);
  };

  const onMultiSelectChangeHandler = (newValues: MultiValue<OptionType>) => {
    const emails: string[] = newValues.map(({ value }: OptionType) => value as string);
    onAccessSelectChangeValidation(emails);
    dispatch(createAction('setEmails', emails));
  };

  const onAllowedSearchChange = (event: SyntheticEvent) => {
    const inputTarget = event.target as HTMLInputElement;
    dispatch(createAction('setAllowedSearch', inputTarget.value));
  };

  const setDashboards = (values: PropsValue<OptionType>) => {
    const dashboards: string[] = Array.isArray(values)
      ? values.map(({ value }: OptionType): string => value as string)
      : [];
    dispatch(createAction('setAllowedDashboards', dashboards));
  };

  const formIsReadyToSubmit = (): boolean => {
    if (state.emails.length > 0 && state.access in USER_ACCESS) {
      if (state.access === USER_ACCESS.LIMITED) {
        return !!state.allowedSearch || state.allowedDashboards.length > 0;
      }
      return true;
    }
    return false;
  };

  const submitDisabled = !formIsReadyToSubmit();

  const submitFunc = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    if (submitDisabled) {
      onAccessSelectChangeValidation(state.emails);
      if (!(state.access in USER_ACCESS)) {
        setAccessErrorValidationMessage('Access is not selected');
      }
      return;
    }
    const { data } = await sendInvites({
      variables: state,
    });
    if (data) {
      await refetchData();
      dispatch(createAction('resetState'));
    }
  };

  const onEmailValidation = (email: string): { isValid: boolean; errorMessage: string } => {
    const isAlreadyPrompted = !!state.emails.map((e) => e.toLowerCase()).includes(email.toLowerCase());
    const isAlreadyAdded = !!userAccountData?.userAccounts.find(
      (userData) => userData.email.toLowerCase() === email.toLowerCase(),
    );

    if (isAlreadyPrompted || isAlreadyAdded) {
      return {
        isValid: false,
        errorMessage: 'That email has already been linked/invited.',
      };
    }
    if (!validEmailFormat(email)) {
      return {
        isValid: false,
        errorMessage: "That doesn't look like a valid email.",
      };
    }
    return {
      isValid: true,
      errorMessage: '',
    };
  };

  const isDisabled = !!disabled;
  return (
    <Form onSubmit={submitFunc} className="UserAddForm">
      <fieldset disabled={isDisabled}>
        <div className="UserAddForm__FirstRowContainer">
          <div className="UserAddForm__EmailContainer">
            <MultiSelectInput
              currentValue={state.emails.map((value) => ({ value, label: value }))}
              placeholder="Enter one or more emails"
              validate={onEmailValidation}
              onChangeHandler={onMultiSelectChangeHandler}
              commonErrorMessage={validationErrorMessage}
              idContainer={ELEMENT.userAccountsEmailInput}
            />
          </div>
          <div className="UserAddForm__AccessSelectWrapper">
            <Select
              onChange={(selectedOption) => {
                const { value } = selectedOption as OptionType;
                onAccessSelectChangeValidation(state.emails);
                dispatch(createAction('setAccess', value));
                if (validationErrorMessage) {
                  setAccessErrorValidationMessage(null);
                }
              }}
              validationErrorMessage={accessErrorValidationMessage}
              value={{ value: state.access, label: ACCESS_OPTIONS_MAP[state.access] }}
              defaultValue={{ value: USER_ACCESS_DEFAULT, label: USER_ACCESS_DEFAULT }}
              isLoading={dashboardLoading}
              options={ACCESS_OPTIONS.map(
                (name: string): OptionType => ({ value: name, label: ACCESS_OPTIONS_MAP[name] }),
              )}
              idContainer={ELEMENT.userAccountsAccessSelect}
            />
          </div>
          <div>
            <Button type="submit" variant={ButtonVariant.PRIMARY} testId={ELEMENT.userAccountsSendInvitesButton}>
              Send Invites
            </Button>
          </div>
        </div>
        {state.access === USER_ACCESS.LIMITED && (
          <div className="UserAddForm__LimitedAccessOptions">
            <div>
              <Form.Label className="UserAddForm__FormLabel">Allowed Search</Form.Label>
              <Input
                onChange={onAllowedSearchChange}
                value={state.allowedSearch}
                placeholder="Enter allowed searches"
                testId={ELEMENT.userAccountsAllowedSearchInput}
              />
            </div>
            <div>
              <Form.Label className="UserAddForm__FormLabel">Allowed Dashboards</Form.Label>
              <Select
                value={state.allowedDashboards.map((name) => ({ value: name, label: name }))}
                isMulti
                onChange={setDashboards}
                isLoading={dashboardLoading}
                options={dashboardsData.dashboardsV2.map(
                  ({ name }: DashboardItem): OptionType => ({ value: name, label: name }),
                )}
                idContainer={ELEMENT.userAccountsDashboardsSelect}
              />
            </div>
          </div>
        )}
      </fieldset>
    </Form>
  );
};
